<script setup lang="ts">
const {
  modelValue = "",
  invalid = false,
  disabled = false,
  required = false,
  theme = "main",
  options,
} = defineProps<{
  modelValue?: string | number;
  invalid?: boolean;
  disabled?: boolean;
  theme?: "main" | "auth" | "overlay";
  required?: boolean;
  options: {
    value: string | number;
    label: string;
  }[];
}>();

const classObject = computed(() => ({
  [`is-theme-${theme}`]: theme,
  [`is-invalid`]: invalid,
  [`is-disabled`]: disabled,
}));

const emit = defineEmits(["update:modelValue", "focus", "blur"]);

const updateValue = (event: Event) => {
  emit("update:modelValue", (event.target as HTMLSelectElement).value);
};
</script>

<template>
  <div class="form-select" :class="classObject">
    <select
      class="element"
      :value="modelValue"
      :disabled="disabled"
      :required="required"
      v-bind="$attrs"
      @change="updateValue"
      @focus="emit('focus')"
      @blur="emit('blur')"
    >
      <option
        v-for="option in options"
        :key="option.value"
        :value="option.value"
      >
        {{ option.label }}
      </option>
    </select>
    <BaseIcon name="chevron_right" class="chevron" />
  </div>
</template>

<style lang="scss" scoped>
.form-select {
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  align-items: center;
  height: 3rem;
  box-sizing: border-box;
  border: solid 2px var(--main-input-border-color);
  border-radius: var(--main-border-radius);
  outline: none;
  background: var(--main-input-background-color);
  color: var(--main-input-text-color);
  transition: border-color 100ms ease;

  .element {
    width: 100%;
    height: 100%;
    border: none;
    outline: none;
    background: none;
    color: var(--main-input-text-color);
    font-size: 16px;
    font-family: "Manrope", sans-serif;
    appearance: none;
    padding: 0 0.75em;

    &::placeholder {
      color: rgba(0, 0, 0, 0.3);
    }
  }

  .chevron {
    rotate: 90deg;
    margin-right: 0.4em;
  }

  &:focus-within {
    border-color: var(--main-input-focus-color);
  }

  &.is-disabled {
    opacity: 0.5;
  }

  &.is-invalid {
    border-color: var(--main-error-color);

    .element {
      color: var(--main-error-color);
    }
  }

  &.is-theme-auth {
    border-color: var(--auth-input-border-color);
    background-color: var(--auth-input-background-color);
    color: var(--auth-input-text-color);

    &:focus-within {
      border-color: var(--auth-input-focus-color);
    }

    &.is-invalid {
      border-color: var(--auth-error-color);

      .element {
        color: var(--auth-error-color);
      }
    }
  }

  &.is-theme-overlay {
    border-color: var(--overlay-input-border-color);
    background-color: var(--overlay-input-background-color);
    color: var(--overlay-input-text-color);

    &:focus-within {
      border-color: var(--overlay-input-focus-color);
    }

    &.is-invalid {
      border-color: var(--overlay-error-color);

      .element {
        color: var(--overlay-error-color);
      }
    }
  }
}
</style>
